import { useMemo, useState } from "react";

import { SCENARIO_ASSIGNMENT_STATUS_STYLES } from "@/components/pages_logged_in/roleplays/utils";
import {
  ScenarioAssignmentBanner,
  SessionAttempts,
} from "@/components/pieces/roleplays/ScenarioAssignmentBanner";
import { Avatar } from "@/components/pieces/users/Avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogInner,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ScenarioAssignmentDisplayStatus,
  ScenarioAssignmentStatistic,
  type UnifiedAssignmentType,
  useMyAssignmentsQuery,
} from "@/graphql";
import { formatDate } from "@/lib/format";

interface SimpleStatisTableWithAssignmentsProps {
  statistics?: ScenarioAssignmentStatistic | null;
  assignments?: Array<UnifiedAssignmentType>;
}

function SimpleStatisTable({
  statistics,
  assignments = [],
}: SimpleStatisTableWithAssignmentsProps) {
  if (!statistics) {
    return (
      <div className="text-sm text-gray-500 p-4">
        No assignment statistics available
      </div>
    );
  }

  const assignmentRows = assignments.map((assignment) => {
    // Handle union type properties safely
    let minAttempt = assignment.minAttempt || 0;
    let maxAttempt = assignment.maxAttempt || 0;

    return {
      assignedBy: assignment?.assignerName || "Unknown",
      progress: assignment?.bestScore ? "Completed" : "Not started",
      due: assignment?.dueDate
        ? new Date(assignment?.dueDate).toLocaleDateString()
        : "",
      status:
        assignment?.displayStatus || ScenarioAssignmentDisplayStatus.NotStarted,
      textColor:
        assignment?.displayStatus === ScenarioAssignmentDisplayStatus.PastDue
          ? "#BF7326"
          : undefined,
      completedCount: assignment?.completedCount || 0,
      minAttempt,
      maxAttempt,
    };
  });

  return (
    <div className="w-full bg-white rounded-md border border-gray-200 overflow-hidden">
      <table className="w-full table-auto">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left px-3 py-2 whitespace-nowrap">
              <div className="text-sm font-semibold text-gray-900">
                Assigner
              </div>
            </th>
            <th className="text-left px-3 py-2 whitespace-nowrap">
              <div className="text-sm font-semibold text-gray-900">
                Progress
              </div>
            </th>
            <th className="text-left px-3 py-2 whitespace-nowrap">
              <div className="text-sm font-semibold text-gray-900">Due</div>
            </th>
            <th className="text-left px-3 py-2 whitespace-nowrap">
              <div className="text-sm font-semibold text-gray-900">Status</div>
            </th>
          </tr>
        </thead>
        <tbody>
          {assignmentRows.map((assignment, index) => {
            const statusKey = assignment.status;
            const statusConfig = SCENARIO_ASSIGNMENT_STATUS_STYLES[statusKey];

            return (
              <tr
                key={index}
                style={{
                  backgroundColor:
                    statusKey === ScenarioAssignmentDisplayStatus.NotStarted
                      ? "white"
                      : statusConfig.backgroundColor,
                }}
                className="border-b border-gray-200"
              >
                {/* Assigned By */}
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Avatar size={24} user={{ name: assignment.assignedBy }} />
                    <span className="text-sm text-gray-900">
                      {assignment.assignedBy}
                    </span>
                  </div>
                </td>

                {/* Progress */}
                <td
                  className="px-3 py-2 text-sm whitespace-nowrap"
                  style={{
                    color: assignment.textColor || "#242424",
                  }}
                >
                  <SessionAttempts
                    completed={assignment.completedCount || 0}
                    minimum={assignment.minAttempt || 0}
                    maximum={assignment.maxAttempt || 0}
                    status={assignment.status}
                  />
                </td>

                {/* Due Date */}
                <td
                  className="px-3 py-2 text-sm whitespace-nowrap"
                  style={{
                    color: assignment.textColor || "#242424",
                  }}
                >
                  {formatDate(assignment.due)}
                </td>

                {/* Status */}
                <td className="px-3 py-2 whitespace-nowrap">
                  <div
                    className="flex items-center px-2 py-1 rounded-sm gap-2 w-fit text-sm"
                    style={{ backgroundColor: statusConfig.backgroundColor }}
                  >
                    {statusConfig.icon}
                    <div style={{ color: statusConfig.textColor }}>
                      {statusConfig.text}
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ScenarioAssignmentInfoContent({ scenarioId }: { scenarioId: string }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { data: myAssignmentsData } = useMyAssignmentsQuery({
    variables: { scenarioId },
    skip: !scenarioId,
    fetchPolicy: "no-cache",
  });

  const assignments = myAssignmentsData?.myAssignments || [];

  // Get the most recent assignment for primary display
  const primaryAssignment = assignments[0];
  const pastDueAssignments = assignments.filter(
    (a) => a?.displayStatus === ScenarioAssignmentDisplayStatus.PastDue,
  );
  const isHavePastDueAssignments = pastDueAssignments.length > 0;

  const assignmentStatusKey = isHavePastDueAssignments
    ? ScenarioAssignmentDisplayStatus.PastDue
    : primaryAssignment?.displayStatus;

  const assignmentStatusStyle =
    assignmentStatusKey &&
    SCENARIO_ASSIGNMENT_STATUS_STYLES[assignmentStatusKey];

  // Convert assignments to statistics-style data for the dropdown
  const assignmentStatistics: ScenarioAssignmentStatistic | null =
    useMemo(() => {
      if (assignments.length === 0) return null;

      const completed = assignments.filter(
        (a) => a?.displayStatus === ScenarioAssignmentDisplayStatus.Completed,
      ).length;
      const pastDue = assignments.filter(
        (a) => a?.displayStatus === ScenarioAssignmentDisplayStatus.PastDue,
      ).length;
      const notStarted = assignments.filter(
        (a) => a?.displayStatus === ScenarioAssignmentDisplayStatus.NotStarted,
      ).length;
      const inProgress = assignments.filter(
        (a) => a?.displayStatus === ScenarioAssignmentDisplayStatus.InProgress,
      ).length;

      return {
        __typename: "ScenarioAssignmentStatistic" as const,
        totalAssignments: assignments.length,
        completedAssignments: completed,
        pastDueAssignments: pastDue,
        notStartedAssignments: notStarted,
        inProgressAssignments: inProgress,
        completionRate:
          assignments.length > 0 ? (completed / assignments.length) * 100 : 0,
      };
    }, [assignments]);

  // Don't render if need to be completed assignments
  if (assignments.length === 0) {
    return null;
  }

  return (
    <div
      className="relative"
      style={{ backgroundColor: assignmentStatusStyle?.backgroundColor }}
    >
      <div className="flex-1">
        <div
          className="flex max-w-[1188px] w-full mx-auto"
          style={{ backgroundColor: assignmentStatusStyle?.backgroundColor }}
        >
          <ScenarioAssignmentBanner
            unifiedAssignments={assignments.filter(
              (a): a is UnifiedAssignmentType => a !== null,
            )}
            onNumberOfAssignmentsClick={() => setIsDialogOpen(true)}
          />
          <>
            {assignmentStatistics && assignments.length > 0 ? (
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-4xl">
                  <DialogHeader className="border-none pb-0">
                    <DialogTitle className="text-base font-semibold">
                      {assignments.length} Active Assignment
                      {assignments.length !== 1 ? "s" : ""}
                    </DialogTitle>
                  </DialogHeader>
                  <DialogInner>
                    <SimpleStatisTable
                      statistics={assignmentStatistics}
                      assignments={assignments.filter(
                        (a): a is UnifiedAssignmentType => a !== null,
                      )}
                    />
                  </DialogInner>
                </DialogContent>
              </Dialog>
            ) : null}
          </>
        </div>
      </div>
    </div>
  );
}

export function ScenarioAssignmentInfo({ scenarioId }: { scenarioId: string }) {
  return <ScenarioAssignmentInfoContent scenarioId={scenarioId} />;
}

export { SimpleStatisTable };
